import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, InputAdornment } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { EstadosRubroChoiceEnum, useLoaders } from '@/shared';
import {
  CreateTransaccionByPaymentRubroData,
  RubroQueryKeyEnum,
  useCreateTransaccion,
  useFetchPaymentMethodsParams,
} from '@/shared/actions/erp';
import {
  CustomAutocomplete,
  CustomNumberTextField,
  CustomTextArea,
} from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { AlertSeverityEnum, IPaymentMethod } from '@/shared/interfaces';
import { addPaymentTransaccionSchema } from '@/shared/utils/validation-schema';
import { useRecaudacionesStore } from '@/store/erp/recaudaciones';
import { useUiConfirmModalStore, useUiStore } from '@/store/ui';

export type ClienteRubroAddPaymentModalProps = {
  open: boolean;
  onClose: () => void;
};

type PaymentTransactionForm = CreateTransaccionByPaymentRubroData & {
  observacion: string;
  totalToPay: string;
};

const ClienteRubroAddPaymentModal: React.FC<
  ClienteRubroAddPaymentModalProps
> = ({ onClose, open }) => {
  ///* hooks ------------------------
  const queryClient = useQueryClient();

  ///* global state ------------------------
  const currentRubro = useRecaudacionesStore(s => s.currentRubro);
  const setCurrentRubro = useRecaudacionesStore(s => s.setCurrentRubro);

  const setAlert = useUiStore(s => s.setAlert);
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  const setIsGlobalLoading = useUiStore(s => s.setIsGlobalLoading);

  ///* form ------------------------
  const form = useForm<PaymentTransactionForm>({
    resolver: yupResolver(addPaymentTransaccionSchema) as any,
  });
  const {
    formState: { errors },
  } = form;
  const watchedTotalToPay = form.watch('totalToPay');

  ///* fetch data ------------------------
  const {
    data: paymentMethodsPagingRes,
    isLoading: isLoadingPaymentMethods,
    isRefetching: isRefetchingPaymentMethods,
  } = useFetchPaymentMethodsParams(
    {
      page_size: 210,
    },
    !!open
  );

  ///* mutations ------------------------
  const createTransaccion =
    useCreateTransaccion<CreateTransaccionByPaymentRubroData>({
      enableRedirect: false,
      customSuccessMessage: `Pago agregado correctamente al rubro ${currentRubro?.numero_referencia}`,
    });

  const createPayment = async (data: PaymentTransactionForm) => {
    return createTransaccion.mutateAsync({
      forma_pago: data.forma_pago,
      total_pagado: data.total_pagado,
      comision: data.comision,
      rubro: currentRubro?.codigo || '',
      cliente: currentRubro?.cliente || '',
      observaciones: [data.observacion],
      contrato: currentRubro?.contrato || '',
      fecha_pago: dayjs().format(),
    });
  };
  const onCompletePayment = async () => {
    handleClose();

    // refetch rubros
    await queryClient.invalidateQueries({
      queryKey: [RubroQueryKeyEnum.RUBROS],
    });
  };

  ///* handlers ------------------------
  const onSave = async (data: PaymentTransactionForm) => {
    const currentRubro = useRecaudacionesStore.getState().currentRubro;

    // // less than total to pay --------------
    const isLessThanTotalToPay = +data.total_pagado < +watchedTotalToPay;
    if (isLessThanTotalToPay) {
      setConfirmDialog({
        isOpen: true,
        title: 'Saldo a favor',
        subtitle: `Está realizando un pago menor al total del rubro ${
          currentRubro?.numero_referencia
        }, si registra el pago quedará un saldo pendiente de $${(
          +watchedTotalToPay - +data.total_pagado
        ).toFixed(2)}`,
        confirmTextBtn: 'Registrar pago',
        onConfirm: async () => {
          setConfirmDialogIsOpen(false);

          setIsGlobalLoading(true);
          await createPayment(data);
          await onCompletePayment();
          setIsGlobalLoading(false);
        },
      });

      return;
    }

    // // bigger than total to pay --------------
    const isBiggerThanTotalToPay = +data.total_pagado > +watchedTotalToPay;
    if (isBiggerThanTotalToPay) {
      setConfirmDialog({
        isOpen: true,
        title: 'Monto mayor',
        subtitle: `Está realizando un pago mayor al total del rubro ${
          currentRubro?.numero_referencia
        }, si registra el pago quedará un saldo a favor de $${(
          +data.total_pagado - +watchedTotalToPay
        ).toFixed(2)}`,
        confirmTextBtn: 'Registrar pago',
        onConfirm: async () => {
          setConfirmDialogIsOpen(false);
          setIsGlobalLoading(true);

          await createPayment(data);
          await onCompletePayment();
          setIsGlobalLoading(false);
        },
      });

      return;
    }

    // // equal to total to pay --------------
    setIsGlobalLoading(true);
    await createPayment(data);
    await onCompletePayment();
    setIsGlobalLoading(false);
  };

  const handleClose = () => {
    form.reset();
    onClose();
    setCurrentRubro(null);
  };

  ///* effects ------------------------
  useEffect(() => {
    if (!currentRubro || !open) return;

    if (
      currentRubro.estado_rubro !== EstadosRubroChoiceEnum.NO_PAGADO &&
      currentRubro.estado_rubro !== EstadosRubroChoiceEnum.PAGO_INCOMPLETO
    ) {
      setAlert(true, 'El rubro ya fue pagado', AlertSeverityEnum.error);

      handleClose();
      return;
    }

    // set total_pagado based on rubro values
    const totalPagado =
      +(currentRubro.valor_total || 0) - +(currentRubro.valor_pagado || 0);

    form.reset({
      rubro: currentRubro.codigo || '',
      cliente: currentRubro.cliente,
      total_pagado: totalPagado.toFixed(2),
      totalToPay: totalPagado.toFixed(2),
      comision: '0.00',
      contrato: currentRubro.contrato || '',
      observacion: '',
    });
  }, [currentRubro]);

  useLoaders(isLoadingPaymentMethods || isRefetchingPaymentMethods);

  if (!open) return null;

  return (
    <ScrollableDialogProps
      open={open}
      title={`Agregar pago al rubro: ${currentRubro?.numero_referencia}`}
      minWidth="40%"
      contentNode={
        <Grid
          item
          xs={12}
          container
          spacing={3}
          mt={2}
          alignItems="center"
          justifyContent="center"
          pb={5}
        >
          <CustomAutocomplete<IPaymentMethod>
            label="Forma de pago"
            name="forma_pago"
            // options
            options={paymentMethodsPagingRes?.data.data || []}
            valueKey="alias"
            actualValueKey="codigo"
            defaultValue={form.getValues().forma_pago || ''}
            optionLabelForEdit={form.getValues().forma_pago || ''}
            isLoadingData={isLoadingPaymentMethods}
            // validation
            control={form.control}
            error={errors.forma_pago}
            helperText={errors.forma_pago?.message}
          />

          <CustomNumberTextField
            name="total_pagado"
            label="Monto"
            defaultValue={form.getValues().total_pagado || ''}
            control={form.control}
            error={errors.total_pagado}
            helperText={errors.total_pagado?.message}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            min={0}
            step={0.01}
          />

          <CustomNumberTextField
            name="comision"
            label="Comisión"
            defaultValue={form.getValues().comision || ''}
            control={form.control}
            error={errors.comision}
            helperText={errors.comision?.message}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            min={0}
            step={0.01}
          />

          <CustomTextArea
            label="Notas"
            name="observacion"
            control={form.control}
            defaultValue={form.getValues('observacion') || ''}
            error={errors.observacion}
            helperText={errors.observacion?.message}
            required={false}
          />
        </Grid>
      }
      onClose={handleClose}
      onConfirm={form.handleSubmit(onSave, err => {
        console.log(err);
        setAlert(true, 'Faltan campos requeridos', AlertSeverityEnum.error);
      })}
    />
  );
};

export default ClienteRubroAddPaymentModal;
